/**
 * Module to add a shipping rates calculator to cart page.
 *
 * Copyright (c) 2011-2012 Caroline Schnapp (11heavens.com)
 * Dual licensed under the MIT and GPL licenses:
 * http://www.opensource.org/licenses/mit-license.php
 * http://www.gnu.org/licenses/gpl.html
 *
 * Modified version -- coupled with Broadcast theme markup
 *
 */

import {AddressForm} from '@shopify/theme-addresses';

import getUrlString from '../util/get-url-string';
import FetchError from '../util/fetch-error';

const texts = {
  error: 'Error : country is not supported.',
  feedback: 'We do not ship to this destination.',
  feedbackLabel: 'Error : ',
};

class ShippingCalculator extends HTMLElement {
  constructor() {
    super();

    this.onCountryChangeEvent = () => this.onCountryChange();
    this.onButtonClickEvent = () => this.onButtonClick();
  }

  connectedCallback() {
    this.getRatesButton = this.querySelector('.get-rates');
    this.fieldsContainer = this.querySelector('#address_container');
    this.selectCountry = this.querySelector('#address_country');
    this.selectProvince = this.querySelector('#address_province');
    this.template = this.querySelector('#shipping-calculator-response-template');
    this.wrapper = this.querySelector('#wrapper-response');
    this.zipInput = this.querySelector('#address_zip');

    const htmlEl = document.querySelector('html');
    let locale = 'en';
    if (htmlEl.hasAttribute('lang') && htmlEl.getAttribute('lang') !== '') {
      locale = htmlEl.getAttribute('lang');
    }

    if (this.fieldsContainer) {
      AddressForm(this.fieldsContainer, locale, {
        shippingCountriesOnly: true,
      });
    }

    if (this.selectCountry && this.selectCountry.hasAttribute('data-default') && this.selectProvince && this.selectProvince.hasAttribute('data-default')) {
      this.selectCountry.addEventListener('change', this.onCountryChangeEvent);
    }

    if (this.getRatesButton) {
      this.getRatesButton.addEventListener('click', this.onButtonClickEvent);

      if (theme.settings.customerLoggedIn && this.getRatesButton.classList.contains('get-rates--trigger')) {
        if (this.zipInput && this.zipInput.value) {
          this.getRatesButton.dispatchEvent(new Event('click'));
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.selectCountry && this.selectCountry.hasAttribute('data-default') && this.selectProvince && this.selectProvince.hasAttribute('data-default')) {
      this.selectCountry.removeEventListener('change', this.onCountryChangeEvent);
    }

    if (this.getRatesButton) {
      this.getRatesButton.removeEventListener('click', this.onButtonClickEvent);
    }
  }

  onCountryChange() {
    this.selectCountry.removeAttribute('data-default');
    this.selectProvince.removeAttribute('data-default');
  }

  onButtonClick() {
    this.disableButtons();
    while (this.wrapper.firstChild) this.wrapper.removeChild(this.wrapper.firstChild);
    this.wrapper.classList.add('hidden');
    const shippingAddress = {};
    let elemCountryVal = this.selectCountry.value;
    let elemProvinceVal = this.selectProvince.value;

    const elemCountryData = this.selectCountry.getAttribute('data-default');
    if (elemCountryVal === '' && elemCountryData && elemCountryData !== '') {
      elemCountryVal = elemCountryData;
    }

    const elemProvinceData = this.selectProvince.getAttribute('data-default');
    if (elemProvinceVal === '' && elemProvinceData && elemProvinceData !== '') {
      elemProvinceVal = elemProvinceData;
    }

    shippingAddress.zip = (this.zipInput && this.zipInput.value) || '';
    shippingAddress.country = elemCountryVal || '';
    shippingAddress.province = elemProvinceVal || '';

    this.getCartShippingRatesForDestination(shippingAddress);
  }

  formatRate(cents) {
    const price = cents === '0.00' ? window.theme.strings.free : window.theme.formatMoney(cents, theme.moneyFormat);
    return price;
  }

  render(response) {
    if (this.template && this.wrapper) {
      this.wrapper.innerHTML = '';
      let ratesList = '';
      let ratesText = '';
      let successClass = `error center`;
      let markup = this.template.innerHTML;
      const rateRegex = /[^[\]]+(?=])/g;

      if (response.rates && response.rates.length) {
        let rateTemplate = rateRegex.exec(markup)[0];
        response.rates.forEach((rate) => {
          let rateHtml = rateTemplate;
          rateHtml = rateHtml.replace(/\|\|rateName\|\|/, rate.name);
          rateHtml = rateHtml.replace(/\|\|ratePrice\|\|/, this.formatRate(rate.price));
          ratesList += rateHtml;
        });
      }

      if (response.success) {
        successClass = `success center`;
        const createdNewElem = document.createElement('div');
        createdNewElem.innerHTML = this.template.innerHTML;
        const noShippingElem = createdNewElem.querySelector('[data-template-no-shipping]');

        if (response.rates.length < 1 && noShippingElem) {
          ratesText = noShippingElem.getAttribute('data-template-no-shipping');
        }
      } else {
        ratesText = response.errorFeedback;
      }

      markup = markup.replace(rateRegex, '').replace('[]', '');
      markup = markup.replace(/\|\|ratesList\|\|/g, ratesList);
      markup = markup.replace(/\|\|successClass\|\|/g, successClass);
      markup = markup.replace(/\|\|ratesText\|\|/g, ratesText);

      this.wrapper.innerHTML += markup;

      this.wrapper.classList.remove('hidden');
    }
  }

  enableButtons() {
    this.getRatesButton.removeAttribute('disabled');
    this.getRatesButton.classList.remove('disabled');
    this.getRatesButton.textContent = theme.strings.shippingCalcSubmitButton;
  }

  disableButtons() {
    this.getRatesButton.setAttribute('disabled', 'disabled');
    this.getRatesButton.classList.add('disabled');
    this.getRatesButton.textContent = theme.strings.shippingCalcSubmitButtonDisabled;
  }

  getCartShippingRatesForDestination(shippingAddress) {
    const encodedShippingAddressData = encodeURI(
      getUrlString({
        shipping_address: shippingAddress,
      })
    );
    const url = `${theme.routes.cart_url}/shipping_rates.json?${encodedShippingAddressData}`;

    fetch(url)
      .then(this.handleErrors)
      .then((response) => response.text())
      .then((response) => {
        const responseJSON = JSON.parse(response);
        const rates = responseJSON.shipping_rates;
        this.onCartShippingRatesUpdate(rates, shippingAddress);
      })
      .catch((error) => {
        this.onError(error.json);
      });
  }

  fullMessagesFromErrors(errors) {
    const fullMessages = [];

    for (const error in errors) {
      for (const message of errors[error]) {
        fullMessages.push(message);
      }
    }

    return fullMessages;
  }

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  onError(data) {
    this.enableButtons();
    let feedback = '';

    if (data.message) {
      feedback = data.message + '(' + data.status + '): ' + data.description;
    } else {
      feedback = texts.feedbackLabel + this.fullMessagesFromErrors(data).join('; ');
    }

    if (feedback === texts.error) {
      feedback = texts.feedback;
    }

    this.render({
      rates: [],
      errorFeedback: feedback,
      success: false,
    });
  }

  onCartShippingRatesUpdate(rates, shippingAddress) {
    this.enableButtons();
    let readableAddress = '';

    if (shippingAddress.zip) {
      readableAddress += shippingAddress.zip + ', ';
    }

    if (shippingAddress.province) {
      readableAddress += shippingAddress.province + ', ';
    }

    readableAddress += shippingAddress.country;

    this.render({
      rates: rates,
      address: readableAddress,
      success: true,
    });
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
